import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { LeaveRequest, LeaveRequestCreate, RelievingOfficerOption, StaffLeaveBalance } from '../../models/staff-module';
import { StaffModuleApiService, StaffModuleError } from '../../services/staff-module-api.service';
import { MyAquilaApiService } from '../../services/my-aquila-api.service';
import { formatDateToYYYYMMDD } from '../../utils/date-utils';
import { NotificationService } from 'src/app/utils/notification.service';

const FORM_FIELDS = ['policyId', 'startDate', 'endDate', 'reason', 'relievingOfficerId', 'reviewerId'];

@Component({
  selector: 'app-apply-leave-modal',
  templateUrl: './apply-leave-modal.component.html',
  styleUrls: ['./apply-leave-modal.component.css']
})
export class ApplyLeaveModalComponent implements OnInit, OnDestroy {
  leaveForm: FormGroup;
  policies: StaffLeaveBalance[] = [];
  officers: RelievingOfficerOption[] = [];
  officerSearch = '';
  /** Staff on a higher salary grade than the applicant (the "Designated Approver" choices). */
  reviewers: RelievingOfficerOption[] = [];
  reviewerSearch = '';
  loadingReviewers = false;
  /**
   * 'loading' → 'ready' (reviewers exist: the field is required), 'none' (the backend returned nobody: the applicant is
   * on the top grade or no grades exist, so no reviewer is needed) or 'failed' (the call errored; the backend decides on submit).
   * The field stays visible in every state so it is clear why it can't be used.
   */
  reviewerStatus: 'loading' | 'ready' | 'none' | 'failed' = 'loading';
  /** Kept for the template and validators: the reviewer is required only when reviewers exist. */
  get reviewerNeeded(): boolean { return this.reviewerStatus === 'ready'; }
  private reviewerBlocked(): boolean { return this.reviewerStatus === 'none' || this.reviewerStatus === 'failed'; }
  get reviewerHint(): string {
    switch (this.reviewerStatus) {
      case 'loading': return $localize`:@@reviewersLoading:Loading approvers…`;
      case 'none': return $localize`:@@noReviewerNeeded:No approver is needed for this request.`;
      case 'failed': return $localize`:@@reviewersFailed:Approvers could not be loaded.`;
      default: return '';
    }
  }
  retryReviewers(): void { this.reviewerStatus = 'loading'; this.loadReviewers(''); }

  loadingPolicies = false;
  loadingOfficers = false;
  submitting = false;
  loadError: string | null = null;
  errorMessage: string | null = null;
  minDate = new Date();
  title = $localize`:@@applyForLeave:Apply for Leave`;
  searchPlaceholder = $localize`:@@searchStaff:Search staff`;
  noStaffLabel = $localize`:@@noStaffFound:No staff found`;

  private staffId: number;
  /** 'me' = My Aquila: the request is filed for the caller via /my-aquila/leave (no staff id). */
  readonly mode: 'staff' | 'me';
  private balanceYear = new Date().getFullYear();
  private search$ = new Subject<string>();
  private reviewerSearch$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<ApplyLeaveModalComponent>,
    private api: StaffModuleApiService,
    private notification: NotificationService,
    private myApi: MyAquilaApiService,
    @Inject(MAT_DIALOG_DATA) public data: { staffId?: number; staff?: any; mode?: 'staff' | 'me' }
  ) {
    this.mode = data?.mode === 'me' ? 'me' : 'staff';
    this.staffId = Number(data?.staffId);
  }

  get isMe(): boolean {
    return this.mode === 'me';
  }

  ngOnInit(): void {
    this.leaveForm = this.formBuilder.group({
      policyId: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      endDate: [null, [Validators.required]],
      relievingOfficerId: [null],
      reviewerId: [null],
      reason: ['', [Validators.maxLength(500)]],
    });

    this.subs.add(this.leaveForm.get('startDate')!.valueChanges.subscribe(value => {
      this.validateEndDate();
      const year = value ? new Date(value).getFullYear() : new Date().getFullYear();
      if (Number.isFinite(year) && year !== this.balanceYear) this.loadPolicies(year);
    }));
    this.subs.add(this.leaveForm.get('endDate')!.valueChanges.subscribe(() => this.validateEndDate()));
    this.subs.add(this.leaveForm.get('policyId')!.valueChanges.subscribe(() => { this.applyOfficerRequirement(); this.applyDaysLeft(); }));
    this.subs.add(
      this.search$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((term) => this.loadOfficers(term)),
    );

    this.subs.add(
      this.reviewerSearch$.pipe(debounceTime(300), distinctUntilChanged()).subscribe((term) => this.loadReviewers(term)),
    );

    // Clear backend field errors / banner as soon as the user edits the form.
    this.subs.add(this.leaveForm.valueChanges.subscribe(() => (this.errorMessage = null)));

    this.loadPolicies();
    this.loadOfficers('');
    this.loadReviewers('');
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  loadPolicies(year = this.balanceYear): void {
    this.balanceYear = year;
    this.loadingPolicies = true;
    this.loadError = null;
    const balances$: Observable<StaffLeaveBalance[]> = this.isMe
      ? this.myApi.leaveBalances(year) as Observable<StaffLeaveBalance[]>
      : this.api.staffLeaveBalances(this.staffId, year);
    balances$
      .pipe(finalize(() => (this.loadingPolicies = false)))
      .subscribe({
        next: (balances) => {
          // Leave types that don't apply to this staff member come back with null numbers: don't offer them.
          this.policies = balances.filter(policy => policy.applicable !== false && policy.available !== null && policy.available !== undefined);
          balances = this.policies;
          this.applyDaysLeft();
          const selected = this.leaveForm.get('policyId');
          if (selected?.value && !balances.some(policy => policy.policyId === selected.value)) selected.setValue(null);
        },
        error: (err: StaffModuleError) => {
          this.loadError = err?.message || $localize`:@@leaveLoadFailed:Something went wrong. Please try again.`;
        },
      });
  }

  onOfficerSearch(term: string): void {
    this.search$.next((term || '').trim());
  }

  onReviewerSearch(term: string): void {
    this.reviewerSearch$.next((term || '').trim());
  }

  /**
   * Loads the possible reviewers. The first (unfiltered) load decides whether the field applies and is required;
   * later loads only narrow the list for the search box. If the call fails the field is hidden and the backend decides.
   */
  private loadReviewers(term: string): void {
    this.loadingReviewers = true;
    const reviewers$: Observable<RelievingOfficerOption[]> = this.isMe
      ? this.myApi.leaveReviewers(term || undefined).pipe(
          map((rows) => (rows ?? []).map((o) => ({ ...(o as any), id: o.id, name: o.name }) as RelievingOfficerOption)),
        )
      : this.api.leaveReviewers(this.staffId, term);
    reviewers$
      .pipe(finalize(() => (this.loadingReviewers = false)))
      .subscribe({
        next: (res) => {
          const list = res ?? [];
          if (!term) this.setReviewerStatus(list.length > 0 ? 'ready' : 'none');
          const selectedId = this.leaveForm.get('reviewerId')?.value;
          const selected = this.reviewers.find((o) => o.id === selectedId);
          this.reviewers = selected && !list.some((o) => o.id === selected.id) ? [selected, ...list] : list;
        },
        error: () => {
          this.reviewers = [];
          this.setReviewerStatus('failed');
        },
      });
  }

  private setReviewerStatus(status: 'ready' | 'none' | 'failed'): void {
    this.reviewerStatus = status;
    const control = this.leaveForm.get('reviewerId');
    if (status === 'ready') {
      control?.addValidators(Validators.required);
      if (!this.noDaysLeft && control?.disabled) control.enable({ emitEvent: false });
    } else {
      control?.removeValidators(Validators.required);
      control?.reset(null, { emitEvent: false });
      control?.disable({ emitEvent: false });
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private loadOfficers(term: string): void {
    this.loadingOfficers = true;
    const officers$: Observable<RelievingOfficerOption[]> = this.isMe
      ? this.myApi.relievingOfficers(term || undefined).pipe(
          map((rows) => (rows ?? []).map((o) => ({ ...(o as any), id: o.id, name: o.name }) as RelievingOfficerOption)),
        )
      : this.api.relievingOfficers(this.staffId, term);
    officers$
      .pipe(finalize(() => (this.loadingOfficers = false)))
      .subscribe({
        next: (res) => {
          const selectedId = this.leaveForm.get('relievingOfficerId')?.value;
          const selected = this.officers.find((o) => o.id === selectedId);
          const list = res ?? [];
          // Keep the current selection in the list even if the search no longer returns it.
          this.officers = selected && !list.some((o) => o.id === selected.id) ? [selected, ...list] : list;
        },
        error: () => {
          // Optional dropdown: leave the list as is; the officer is only required by some policies.
        },
      });
  }

  get selectedPolicy(): StaffLeaveBalance | undefined {
    const id = this.leaveForm?.get('policyId')?.value;
    return this.policies.find((p) => p.policyId === id);
  }

  /**
   * Working days between the chosen dates (inclusive), counting Monday to Friday. Null until both dates are valid.
   * Public holidays aren't known here, so the server's own count is authoritative on submit.
   */
  get calculatedWorkingDays(): number | null {
    const start = this.leaveForm?.get('startDate')?.value;
    const end = this.leaveForm?.get('endDate')?.value;
    if (!start || !end) return null;
    const cursor = new Date(start);
    const last = new Date(end);
    if (isNaN(cursor.getTime()) || isNaN(last.getTime()) || last < cursor) return null;
    cursor.setHours(0, 0, 0, 0);
    last.setHours(0, 0, 0, 0);
    let days = 0;
    // guard against absurd ranges producing a long loop
    for (let i = 0; cursor <= last && i < 800; i++) {
      const weekday = cursor.getDay();
      if (weekday !== 0 && weekday !== 6) days++;
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  /** The calculated days are more than the days available on the chosen leave type. */
  get exceedsAvailable(): boolean {
    const days = this.calculatedWorkingDays;
    const policy = this.selectedPolicy;
    return days !== null && !!policy && days > (policy.available ?? 0);
  }

  get requiresReliever(): boolean {
    return this.selectedPolicy?.requiresRelievingOfficer || false;
  }

  /** True when the chosen leave type has no days left to draw on. */
  get noDaysLeft(): boolean {
    const policy = this.selectedPolicy;
    return !!policy && (policy.available ?? 0) <= 0;
  }

  /** No days left on the chosen leave type: clear and lock every field except the Leave Category; otherwise unlock them. */
  private applyDaysLeft(): void {
    const names = Object.keys(this.leaveForm.controls).filter(name => name !== 'policyId');
    const controls = names.map(name => this.leaveForm.get(name));
    if (this.noDaysLeft) {
      names.forEach(name => {
        const control = this.leaveForm.get(name);
        control?.reset(name === 'reason' ? '' : null, { emitEvent: false });
        control?.disable({ emitEvent: false });
      });
    } else {
      names.forEach(name => {
        const control = this.leaveForm.get(name);
        // The reviewer stays disabled while there is nobody to choose from.
        if (name === 'reviewerId' && (this.reviewerBlocked() || this.reviewerStatus === 'loading')) return;
        if (control?.disabled) control.enable({ emitEvent: false });
      });
    }
  }

  private applyOfficerRequirement(): void {
    const control = this.leaveForm.get('relievingOfficerId');
    if (this.requiresReliever) {
      control?.setValidators([Validators.required]);
    } else {
      control?.clearValidators();
    }
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private validateEndDate(): void {
    const start = this.leaveForm.get('startDate')?.value;
    const endControl = this.leaveForm.get('endDate');
    const end = endControl?.value;
    if (start && end && end < start) {
      endControl?.setErrors({ ...(endControl.errors || {}), invalidEndDate: true });
    } else if (endControl?.hasError('invalidEndDate')) {
      const { invalidEndDate, ...rest } = endControl.errors || {};
      endControl.setErrors(Object.keys(rest).length ? rest : null);
    }
  }

  policyLabel(policy: StaffLeaveBalance): string {
    return `${policy.policyName} (${policy.available} ${$localize`:@@leaveDaysAvailable:available`} ${$localize`:@@leaveDaysOf:of`} ${policy.entitled})`;
  }

  fieldError(name: string): string | null {
    return this.leaveForm.get(name)?.getError('server') ?? null;
  }

  readonly noDaysLeftMessage = $localize`:@@leaveNoDaysLeft:You have no days left on this leave type.`;

  get submitDisabled(): boolean {
    return !this.leaveForm?.valid || this.noDaysLeft || this.submitting || this.loadingPolicies;
  }

  get submitDisabledReason(): string {
    if (!this.submitDisabled) return '';
    if (this.submitting) return $localize`:@@leaveSubmittingTooltip:Your leave application is being submitted.`;
    if (this.loadingPolicies) return $localize`:@@leavePoliciesLoadingTooltip:Leave policies are still loading.`;
    if (this.loadError) return this.loadError;
    if (this.noDaysLeft) return this.noDaysLeftMessage;

    const policy = this.leaveForm?.get('policyId');
    const start = this.leaveForm?.get('startDate');
    const end = this.leaveForm?.get('endDate');
    const reliever = this.leaveForm?.get('relievingOfficerId');
    const reviewer = this.leaveForm?.get('reviewerId');
    if (policy?.hasError('required')) return $localize`:@@leaveSubmitSelectCategory:Select a leave category.`;
    if (start?.hasError('required')) return $localize`:@@leaveSubmitSelectStartDate:Select a start date.`;
    if (end?.hasError('required')) return $localize`:@@leaveSubmitSelectEndDate:Select an end date.`;
    if (end?.hasError('invalidEndDate')) return $localize`:@@leaveSubmitInvalidEndDate:The end date must be on or after the start date.`;
    if (reliever?.hasError('required')) return $localize`:@@leaveSubmitSelectReliever:Select a relieving officer for this leave category.`;

    if (reviewer?.hasError('required')) return $localize`:@@leaveSubmitSelectReviewer:Choose who will review this leave request.`;

    const serverMessage = FORM_FIELDS.map(name => this.fieldError(name)).find(Boolean);
    return serverMessage || $localize`:@@leaveSubmitCompleteRequired:Complete all required fields before submitting.`;
  }

  cancel() {
    this.dialogRef.close();
  }

  submit() {
    if (this.submitting || this.noDaysLeft) return;
    if (!this.leaveForm.valid) {
      this.leaveForm.markAllAsTouched();
      return;
    }
    const v = this.leaveForm.value;
    this.submitting = true;
    this.errorMessage = null;
    const request = {
        policyId: v.policyId,
        startDate: formatDateToYYYYMMDD(v.startDate),
        endDate: formatDateToYYYYMMDD(v.endDate),
        ...((v.reason || '').trim() && { reason: v.reason.trim() }),
        ...(v.relievingOfficerId && { relievingOfficerId: v.relievingOfficerId }),
        ...(v.reviewerId && { reviewerId: v.reviewerId }),
      };
    const create$: Observable<LeaveRequest> = this.isMe
      ? this.myApi.createLeaveRequest(request as LeaveRequestCreate)
      : this.api.createStaffLeaveRequest(this.staffId, request);
    create$
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: (created) => {
          this.notification.success($localize`:@@leaveRequestSubmitted:Leave request submitted successfully`);
          this.dialogRef.close({ created });
        },
        error: (err: StaffModuleError) => this.handleError(err),
      });
  }

  private handleError(err: StaffModuleError): void {
    let mapped = false;
    for (const [key, message] of Object.entries(err.fieldErrors ?? {})) {
      const control = FORM_FIELDS.includes(key) ? this.leaveForm.get(key) : null;
      if (control) {
        control.setErrors({ ...(control.errors || {}), server: message });
        control.markAsTouched();
        mapped = true;
      }
    }
    // Business-rule messages (insufficient balance, notice period, overlap...) have no field key.
    if (!mapped) {
      this.errorMessage = err.message;
    }
  }
}
